import React, { AnchorHTMLAttributes } from "react";

import Button from "components/button";

const ExternalLink = (props: AnchorHTMLAttributes<HTMLAnchorElement>) => (
  <a target="_blank" rel="noreferrer" className="text-primary" {...props} />
);

export const faqData = [
  {
    question: "How do I get support for the Enerflo Operating System?",
    answer: (
      <>
        Log in to Enerflo and open the chat widget to connect with Flo or a live support specialist. Chat response times
        average under one minute during support hours (Mon–Sat, 6am–7pm PT). For non-urgent questions, email{" "}
        <a href="mailto:support@enerflo.io" className="text-primary">
          support@enerflo.io
        </a>
        .
      </>
    ),
  },
  {
    question: "What is Flo, and how does it support my team?",
    answer:
      "Flo is Enerflo's AI assistant, the first layer of chat support. Flo is trained on the Enerflo OS and understands residential solar workflows including deal flow, lender integrations, contract management, and install tracking. When a question needs a human, a specialist steps in automatically. Flo earned a 100% satisfaction score in its most recent reporting period.",
  },
  {
    question: "What are Enerflo's support hours?",
    answer:
      "Support is available Monday through Saturday, 6:00am to 7:00pm Pacific Time (9:00am to 10:00pm Eastern). Support is closed on Sundays.",
  },
  {
    question: "Does Enerflo support help with lender integrations and financing setup?",
    answer:
      "Yes. Enerflo's support team is fluent in the solar financing ecosystem: loan products, TPO (lease/PPA) configurations, integrated lender portals, soft credit pulls, and smart pulls. If your team has questions about how financing flows through the OS, our specialists are equipped to help.",
  },
  {
    question: "Where can I find training resources for my sales reps?",
    answer:
      "The Enerflo Learning Platform offers comprehensive training videos for sales reps and ops teams. Access it by logging in to Enerflo and clicking the Learning Platform link in your account dropdown. The Enerflo Help Center also contains articles on sales training, scheduling, roles and permissions, and EPC settings.",
  },
  {
    question: "Can developers access Enerflo's API documentation?",
    answer: (
      <>
        Yes. API documentation, webhook references, and integration guides are available at{" "}
        <ExternalLink href="https://docs.enerflo.io">docs.enerflo.io</ExternalLink>. Enerflo connects 100+ third-party
        tools and apps across the residential solar project lifecycle.
      </>
    ),
  },
  {
    question: "How does Enerflo handle downtime or OS outages?",
    answer: (
      <>
        When issues occur, status updates are communicated in real time. Infrastructure-level transparency is a core
        principle of how we operate. You can visit{" "}
        <ExternalLink href="https://trust.enerflo.com">trust.enerflo.com</ExternalLink> for current security and
        reliability information.
      </>
    ),
  },
  {
    question: "I'm not yet an Enerflo partner. Can support help me evaluate the OS?",
    answer: (
      <>
        For pre-sales questions, book a discovery call at{" "}
        <Button href="/demo" variant="link">
          enerflo.com/demo
        </Button>
        . An Enerflo OS specialist will walk through how the system connects your tools, apps, and deal data, from first
        knock to PTO, for your specific business model.
      </>
    ),
  },
  {
    question: "Does Enerflo support work with my existing CRM?",
    answer:
      "Yes, and this is an area where Enerflo goes well beyond standard support. Enerflo integrates with the leading CRMs used across residential solar, and our dedicated professional services team specializes in CRM integrations specifically. They're not generalists: this team has deep, hands-on experience with complex CRM configurations and is focused on monitoring those integrations for business continuity so your deal data keeps flowing without interruption. If you're connecting Enerflo to your existing CRM, whether at onboarding or as your stack evolves, you'll have a highly experienced team managing it, not just a help article.",
  },
  {
    question: "How long does Enerflo onboarding take?",
    answer:
      "Most companies are live and running deals in the Enerflo OS within a few weeks. That said, we'll be straight with you: it takes roughly 90 days of committed usage before the compounding value really kicks in: cleaner deal data, fewer downstream change orders, faster install timelines, and reporting you can trust. The onboarding itself is structured and supported. What determines the timeline more than anything is leadership commitment to adoption. Teams that enforce usage early get to value faster. We're with you for all of it with onboarding calls, Help Center resources, the Learning Platform for your reps, and our support team available Monday through Saturday.",
  },
  {
    question: "What's the difference between the Help Center and the Learning Platform?",
    answer: (
      <>
        They serve different needs. The Help Center is a reference library, with articles covering platform settings,
        roles and permissions, EPC configurations, scheduling, and specific feature questions. It's built for operators
        and admins who need answers fast. The Learning Platform is structured training with video-based courses designed
        specifically for sales reps to get productive on the OS quickly. If your reps are new to Enerflo or you're
        onboarding a new sales org, the Learning Platform is where they should start. Both are available anytime: the
        Help Center at <ExternalLink href="https://intercom.help/enerflo/en">intercom.help/enerflo/en</ExternalLink> and
        the Learning Platform inside Enerflo via your account dropdown.
      </>
    ),
  },
];
